import { useCallback, useEffect, useMemo, useState } from 'react'

import { useSelector } from 'react-redux'

import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../../reducers/UserReducer'
import SchoolRequestsTable from './SchoolRequestsTable'
import { DEFAULT_SCHOOL_REQUEST_FILTERS } from './SchoolRequestFilters'
import AcceptSchoolRequestModal from './AcceptSchoolRequestModal'
import InternshipRequestRefusalModal from '../InternshipRequestsView/InternshipRequestRefusalModal'
import {
  acceptSchoolPortalRequest, getAllSchoolPortalRequests, refuseSchoolPortalRequest
} from '../../../../utils/api/schoolPortalRequest'
import { SchoolPortalRequest } from '../../../../utils/entities/schoolPortalRequest'
import { onError, onSuccess } from '../../../../utils/apiHelper'
import { TableParameters } from '../../../../utils/entities/tableParameters'
import { OrderBy, OrderDirections } from '../../../../utils/constants'
import { useLocalStorage } from '../../../../hooks/UseLocalStorage'
import LOCAL_STORAGE_KEYS from '../../../../utils/localStorage'
import { retrieveMetadata } from '../../../../utils/http'

const SchoolRequestsView = () => {
  const user = useSelector(selectUser)
  const t = useSelector(selectTranslate)

  const [schoolRequests, setSchoolRequests] = useState({ data: [], metadata: {} })
  const [loading, setLoading] = useState(true)
  const [requestToProcess, setRequestToProcess] = useState(null)
  const [requestToConsult, setRequestToConsult] = useState(null)
  const [requestToRefuse, setRequestToRefuse] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)

  const [rawParameters, setRawParameters] = useLocalStorage(
    LOCAL_STORAGE_KEYS.SCHOOL_REQUESTS.PARAMETERS,
    new TableParameters({
      order: { by: OrderBy.CREATED_AT, direction: OrderDirections.DESC },
      pageSize: 20,
      filters: { ...DEFAULT_SCHOOL_REQUEST_FILTERS }
    })
  )

  const parameters = useMemo(
    () => new TableParameters({ ...rawParameters, metadata: schoolRequests.metadata }),
    [rawParameters, schoolRequests.metadata]
  )

  const fetchSchoolRequests = useCallback(parameters => {
    setLoading(true)

    getAllSchoolPortalRequests(user, parameters.toQueryParameters()).then(({ data, meta }) => {
      setSchoolRequests({
        data: (data ?? []).map(request => new SchoolPortalRequest(request)),
        metadata: retrieveMetadata(meta)
      })
    }).finally(() => setLoading(false))
  }, [user.id])

  useEffect(() => {
    fetchSchoolRequests(parameters)
  }, [fetchSchoolRequests, parameters.filters, parameters.page, parameters.order, parameters.pageSize])

  const handleAccept = useCallback(requests => {
    setActionLoading(true)

    acceptSchoolPortalRequest(user, requestToProcess, requests).then(() => {
      onSuccess(t('school_requests_table.accept.success'))
      setRequestToProcess(null)
      fetchSchoolRequests(parameters)
    }).catch(() => {
      onError(t('school_requests_table.accept.error'))
    }).finally(() => setActionLoading(false))
  }, [user, t, requestToProcess, fetchSchoolRequests, parameters])

  const handleRefuse = useCallback(refusalReason => {
    setActionLoading(true)

    refuseSchoolPortalRequest(user, requestToRefuse, refusalReason).then(() => {
      onSuccess(t('school_requests_table.refuse.success'))
      setRequestToRefuse(null)
      fetchSchoolRequests(parameters)
    }).catch(() => {
      onError(t('school_requests_table.refuse.error'))
    }).finally(() => setActionLoading(false))
  }, [user, t, requestToRefuse, fetchSchoolRequests, parameters])

  return (
    <>
      <SchoolRequestsTable
        data={schoolRequests.data}
        loading={loading}
        metadata={schoolRequests.metadata}
        parameters={parameters}
        onAccept={setRequestToProcess}
        onConsult={setRequestToConsult}
        onFiltersChange={filters => setRawParameters(old => ({ ...old, filters, page: 1 }))}
        onParametersChange={newParameters => setRawParameters(old => ({ ...old, ...newParameters }))}
        onRefuse={setRequestToRefuse}
        onResetFilters={() => setRawParameters(old => ({
          ...old,
          filters: { ...old.filters, ...DEFAULT_SCHOOL_REQUEST_FILTERS },
          page: 1
        }))}
      />

      <AcceptSchoolRequestModal
        loading={actionLoading}
        schoolPortalRequest={requestToProcess}
        onCancel={() => setRequestToProcess(null)}
        onConfirm={handleAccept}
      />

      <AcceptSchoolRequestModal
        readOnly
        schoolPortalRequest={requestToConsult}
        onCancel={() => setRequestToConsult(null)}
      />

      <InternshipRequestRefusalModal
        loading={actionLoading}
        visible={requestToRefuse !== null}
        onCancel={() => setRequestToRefuse(null)}
        onConfirm={handleRefuse}
      />
    </>
  )
}

export default SchoolRequestsView
